import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import DocumentsModal from './documentsModal/DocumentsModal';
import './Documentation.scss';
import { i18n } from 'core/i18n';
import { DocumentList } from './DocumentList';
import { DocumentationButton } from './DocumentationButton';
import { DocumentationHiddenButton } from './DocumentationHiddenButton';
import { useDocumentsModal } from './hooks/useDocumentsModal';
import { updateDocumentsState } from './helper/updateDocumentsState.helper';
import { DocumentModalLoading } from './documentsModal/DocumentModalLoading';

const { formatText, FormattedText } = i18n

export const Documentation = ({
    getDocuments,
    docReady,
    initialDocumentsList,
    onSaveDocumentAcceptance,
    aDocumentWasAccepted,
    aDocumentWasNotAccepted,
    isLoading,
    precontractualLabel,
    onAcceptAllDocument
}) => {
    const isMount = React.useRef(true);
    const [documentsList, setDocumentsList] = useState(initialDocumentsList.toJS());
    const [loadingDocument, setLoadingDocument] = useState(false);

    const isAllDocumentsAccepted = useMemo(
        () => Array.isArray(documentsList) && documentsList.every(document => document.isAccepted),
        [documentsList]
    );

    const handleLoadDocuments = () => {
        getDocuments();
    }

    const {
        showDocumentModal,
        documentsToOpen,
        documentToOpen,
        setDocumentsToOpen,
        handleCloseDocumentsModal,
        handleShowDocumentsModal,
        handleOpenDocumentsModal
    } = useDocumentsModal({
        documentsList,
        isDocumentsLoaded: docReady,
        onLoadDocuments: handleLoadDocuments,
    });

    useEffect(() => {
        const documents = initialDocumentsList.toJS();
        const initialDocumentsListHasFile = documents.every(document => document.file);

        if (docReady && documentsToOpen.length === 0 && initialDocumentsListHasFile) {
            const mappedDocuments = documents.map((document, index) => ({
                ...document,
                fileName: formatText(`precontractualDocuments-${document.documentName}`),
                title: `${formatText(`precontractualDocuments-${document.documentName}`)} (${index + 1}/${documents.length})`,
            }))

            setDocumentsList(mappedDocuments);
            setLoadingDocument(false)
            !isMount.current && handleOpenDocumentsModal(mappedDocuments, documentToOpen);
        }

        isMount.current = false
    }, [docReady, documentsToOpen, initialDocumentsList])

    useEffect(() => {
        if (isLoading) {
            setLoadingDocument(true)
        }
    }, [isLoading])

    useEffect(() => {
        if (isAllDocumentsAccepted) {
            onAcceptAllDocument && onAcceptAllDocument()
        }
    }, [isAllDocumentsAccepted])

    const handleDocumentRead = documentReaded => {
        setDocumentsList(updateDocumentsState(documentReaded.dmsDocumentType, { isRead: true }));
        setDocumentsToOpen(updateDocumentsState(documentReaded.dmsDocumentType, { isRead: true }));
    };
    const handleDocumentAccepted = documentAccepted => {
        setDocumentsList(updateDocumentsState(documentAccepted.dmsDocumentType, { isAccepted: true }));
    };

    return (
        <>
            <div className="documentation-docs-container">
                <div className="documentation-docs-container-information">
                    <p>{formatText('contractsView-contractsView_precontractualConditionsInformation1')}</p>
                </div>
                <div>
                    <p><FormattedText value="contractsView-contractsView_precontractualConditionsInformation2" /></p>
                    <DocumentList documentsList={documentsList} onClickDocumentItem={handleShowDocumentsModal} />
                    <DocumentationHiddenButton
                        isAllDocumentsAccepted={isAllDocumentsAccepted}
                    />
                </div>
                {!isAllDocumentsAccepted && (
                    <DocumentationButton documentsList={documentsList} onClick={() => handleShowDocumentsModal()} />
                )}
            </div>

            {isAllDocumentsAccepted && (
                <div>
                    <p className="documentation-text">
                        {precontractualLabel}
                    </p>
                </div>
            )}

            <DocumentModalLoading isLoading={loadingDocument} />

            <DocumentsModal
                documentsList={documentsToOpen}
                showDocumentModal={showDocumentModal}
                handleClose={handleCloseDocumentsModal}
                onDocumentAccepted={handleDocumentAccepted}
                onSaveDocumentAcceptance={onSaveDocumentAcceptance}
                onDocumentRead={handleDocumentRead}
                aDocumentWasAccepted={aDocumentWasAccepted}
                aDocumentWasNotAccepted={aDocumentWasNotAccepted}
            />
        </>
    );
};

Documentation.propTypes = {
    docReady: PropTypes.bool,
    getDocuments: PropTypes.func,
    initialDocumentsList: PropTypes.array,
    onSaveDocumentAcceptance: PropTypes.func,
};
